
import AdminLayout from "@/components/layout/AdminLayout";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { format } from "date-fns";
import { PrintsFilters } from "@/components/prints/PrintsFilters";
import { PrintFilters } from "@/components/prints/types";
import { PrintsList } from "@/components/prints/PrintsList";
import { PrintStatus } from "@/integrations/supabase/types/common";

const ITEMS_PER_PAGE = 10;

export default function Prints() {
  const [filters, setFilters] = useState<PrintFilters>({
    artwork: "",
    artist: "",
    date: undefined,
    provider_id: "all",
    status: "all",
    certified: "all",
    creator_id: "all",
    print_type: "all", // Filter for print types
  });

  const [currentPage, setCurrentPage] = useState(1);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["prints-list", filters, currentPage],
    queryFn: async () => {
      console.log("Fetching prints with filters:", filters, "page:", currentPage);
      
      let standardPrints = [];
      let standardPrintsCount = 0;
      let specialPrints = [];
      let specialPrintsCount = 0;

      // Base query for standard prints with editions
      if (filters.creator_id === 'all' || filters.creator_id) {
        let query = supabase
          .from("prints")
          .select(`
            print_id,
            edition_number,
            certified,
            status,
            certificate_id,
            caption,
            print_date,
            certificates (
              hologram_number
            ),
            media (
              media_name
            ),
            print_service_providers (
              name
            ),
            highres_file (
              file_id,
              original_filename
            ),
            editions!inner (
              edition_name,
              edition_limit,
              width,
              height,
              top_margin,
              bottom_margin,
              left_margin,
              right_margin,
              artworks!inner (
                title,
                creator_id,
                preview,
                creation_date,
                art_technique,
                creators!inner (
                  name,
                  surname,
                  nick_name
                )
              )
            )
          `, { count: 'exact' });

        // Apply basic filters
        if (filters.artwork) {
          query = query.ilike('editions.artworks.title', `%${filters.artwork}%`);
        }

        if (filters.artist) {
          query = query.or(`editions.artworks.creators.name.ilike.%${filters.artist}%,editions.artworks.creators.surname.ilike.%${filters.artist}%`);
        }

        if (filters.date) {
          const dateStr = format(filters.date, 'yyyy-MM-dd');
          query = query
            .gte('print_date', `${dateStr}T00:00:00`)
            .lte('print_date', `${dateStr}T23:59:59`);
        }

        if (filters.provider_id !== 'all') {
          query = query.eq('provider_id', parseInt(filters.provider_id));
        }

        if (filters.status !== "all") {
          const status = filters.status as any;
          query = query.eq('status', status);
        }

        if (filters.certified !== "all") {
          query = query.eq('certified', filters.certified === 'certified');
        }

        // Apply creator filter with inner join to ensure only matching records
        if (filters.creator_id !== 'all') {
          query = query.eq('editions.artworks.creator_id', parseInt(filters.creator_id));
        }

        // Filter by print type
        if (filters.print_type !== "all") {
          if (filters.print_type === "test") {
            query = query.ilike('caption', '%Test Print%');
          } else if (filters.print_type === "sample") {
            query = query.ilike('caption', '%Sample Print%');
          } else if (filters.print_type === "standard") {
            query = query.not('caption', 'ilike', '%Test Print%')
                        .not('caption', 'ilike', '%Sample Print%');
          }
        }

        // Get standard prints (with editions)
        const standardPrintsQuery = await query;
        standardPrints = standardPrintsQuery.data || [];
        standardPrintsCount = standardPrintsQuery.count || 0;
      }
      
      // Only fetch special prints if we are showing all prints or specifically test/sample prints
      // and if we're not filtering by creator or we have a creator filter
      if ((filters.print_type === "all" || filters.print_type === "test" || filters.print_type === "sample") &&
          (filters.creator_id === 'all' || filters.creator_id)) {
        // Create a separate query for prints without editions but with artwork info through highres_file
        let specialQuery = supabase
          .from("prints")
          .select(`
            print_id,
            edition_number,
            certified,
            status,
            certificate_id,
            caption,
            print_date,
            certificates (
              hologram_number
            ),
            media (
              media_name
            ),
            print_service_providers (
              name
            ),
            highres_file!inner (
              file_id,
              original_filename,
              artworks!inner (
                title,
                creator_id,
                preview,
                creation_date,
                art_technique,
                creators!inner (
                  name,
                  surname,
                  nick_name,
                  certificate_template
                )
              )
            ),
            editions (
              edition_name,
              edition_limit,
              width,
              height,
              top_margin,
              bottom_margin,
              left_margin,
              right_margin
            )
          `, { count: 'exact' })
          .is('editions', null);

        // Apply same basic filters as for standard prints
        if (filters.provider_id !== 'all') {
          specialQuery = specialQuery.eq('provider_id', parseInt(filters.provider_id));
        }

        if (filters.status !== "all") {
          const status = filters.status as any;
          specialQuery = specialQuery.eq('status', status);
        }

        if (filters.certified !== "all") {
          specialQuery = specialQuery.eq('certified', filters.certified === 'certified');
        }

        // Apply creator filter using inner join to ensure only matching records
        if (filters.creator_id !== 'all') {
          specialQuery = specialQuery.eq('highres_file.artworks.creator_id', parseInt(filters.creator_id));
        }

        // Filter by artwork title if specified
        if (filters.artwork) {
          specialQuery = specialQuery.ilike('highres_file.artworks.title', `%${filters.artwork}%`);
        }

        // Filter by artist name if specified
        if (filters.artist) {
          specialQuery = specialQuery.or(`highres_file.artworks.creators.name.ilike.%${filters.artist}%,highres_file.artworks.creators.surname.ilike.%${filters.artist}%`);
        }

        // Filter by print type for special prints
        if (filters.print_type === "test") {
          specialQuery = specialQuery.ilike('caption', '%Test Print%');
        } else if (filters.print_type === "sample") {
          specialQuery = specialQuery.ilike('caption', '%Sample Print%');
        }

        // Execute the special prints query
        const specialPrintsQuery = await specialQuery;
        specialPrints = specialPrintsQuery.data || [];
        specialPrintsCount = specialPrintsQuery.count || 0;
      }
      
      // Map special prints to match the structure of standard prints
      const formattedSpecialPrints = specialPrints.map(print => {
        // Get the first artwork associated with this highres_file (should only be one)
        const artwork = print.highres_file?.artworks?.[0] || null;
        
        return {
          ...print,
          // Create a synthetic editions structure that matches the standard print structure
          editions: {
            ...(print.editions || {}),
            artworks: artwork ? {
              title: artwork.title,
              creator_id: artwork.creator_id,
              preview: artwork.preview,
              creation_date: artwork.creation_date,
              art_technique: artwork.art_technique,
              creators: artwork.creators
            } : null
          }
        };
      });
      
      // Combine the results
      const allPrints = [...standardPrints, ...formattedSpecialPrints];
      const totalCount = standardPrintsCount + specialPrintsCount;
      
      // Sort all prints by print_date
      allPrints.sort((a, b) => {
        const dateA = new Date(a.print_date).getTime();
        const dateB = new Date(b.print_date).getTime();
        return dateB - dateA; // Sort in descending order (newest first)
      });
      
      // Calculate maximum valid page
      const maxPage = Math.ceil(totalCount / ITEMS_PER_PAGE);
      const validPage = Math.min(currentPage, maxPage || 1);
      
      // Apply pagination manually since we've combined results
      const from = (validPage - 1) * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE;
      const paginatedPrints = allPrints.slice(from, to);

      console.log("Fetched prints:", paginatedPrints, "total count:", totalCount);
      return {
        prints: paginatedPrints,
        totalCount: totalCount,
        currentPage: validPage
      };
    },
  });

  const totalPages = Math.ceil((data?.totalCount || 0) / ITEMS_PER_PAGE);

  const handlePageChange = (page: number) => {
    // Ensure page is within valid range
    const validPage = Math.min(Math.max(1, page), Math.max(1, totalPages));
    setCurrentPage(validPage);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Prints</h1>
        </div>

        <PrintsFilters filters={filters} onFiltersChange={setFilters} />

        {isLoading ? (
          <div>Loading prints...</div>
        ) : error ? (
          <div className="text-red-500">Error loading prints. Please try again.</div>
        ) : (
          <PrintsList 
            prints={data?.prints || []} 
            currentPage={data?.currentPage || 1}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </AdminLayout>
  );
}
